//@ts-check
import { Browser } from '../utils/browsercheck';
import { slideDown, slideToggle, slideUp } from '../utils/effects';

/** @import { MenuOptions } from "../components/menu" */
export class DropdownMenu {
    header;

    /** The root element of the menu. */
    root;

    /** The burger menu */
    navButton;

    /** @type {{ element: HTMLElement, close: HTMLElement, scroller: HTMLElement } | null} */
    overlay = null;

    /** @type {HTMLElement[]} Seitenbereiche, die bei offenem Overlay inert gesetzt wurden */
    #inertElements = [];

    /** The menu options */
    options;

    /** @type {Array<Submenu>} */
    submenus = [];

    /** @type {NodeListOf<HTMLAnchorElement> } AllLinks from menu - all */
    links;

    skipNav;

    /** @type {'mobile' | 'desktop'} */
    modeBeforeResize;

    /**
     * @param { MenuOptions } options
     */
    constructor(options) {
        this.options = options;
        this.root = this.#query(options.selector);
        this.header = this.root.closest('#header');
        this.navButton = this.#query(options.navButton);
        this.links = this.#queryAll(`${options.selector} a`);
        this.skipNav = this.#query('a[href*="skipNavigation"]');
        this.modeBeforeResize = this.inMobileMode() ? 'mobile' : 'desktop';

        if (options.overlay) {
            const element = this.#query(options.overlay.element);

            this.overlay = {
                element,
                close: this.#query(options.overlay.close),
                // Ohne eigenen Scroll-Container scrollt das Overlay selbst
                scroller: options.overlay.scroller ? this.#query(options.overlay.scroller) : element,
            };
        }

        this.#getSubmenus();
        this.#registerEvents();

        if (this.inMobileMode()) {
            this.openTrail();
        }
    }

    inMobileMode() {
        return getComputedStyle(this.navButton).display !== 'none';
    }

    toggleMenu() {
        slideToggle(this.root, 200, () => {
            this.header?.classList.toggle('menu-open');
        });

        this.navButton.classList.toggle('active');
    }

    closeMenu() {
        if (this.header?.classList.contains('menu-open')) {
            this.toggleMenu();
        }
    }

    openTrail() {
        for (const submenu of this.submenus) {
            if (submenu.state.isTrail && !submenu.state.isOpen) {
                submenu.openLevel();
            }
        }
    }

    /**
     * Overlay-Modus (Vollbild-Navigation): öffnet/schließt das Overlay, spiegelt den Zustand am Burger-Button
     * (.active, aria-expanded), sperrt das Seiten-Scrolling (html.nav-overlay-open) und verwaltet den Fokus.
     */
    toggleOverlay() {
        this.isOverlayOpen() ? this.closeOverlay() : this.openOverlay();
    }

    isOverlayOpen() {
        return this.overlay?.element.classList.contains('open') ?? false;
    }

    openOverlay() {
        if (!this.overlay || this.isOverlayOpen()) return;

        this.overlay.element.classList.add('open');
        this.navButton.classList.add('active');
        this.navButton.setAttribute('aria-expanded', 'true');
        // Scroll-Sperre des Themes (base/_base.scss: overflow clip + scrollbar-gutter stable → Seitenbreite bleibt gleich,
        // Overlay-Inhalt liegt deckungsgleich über dem Header)
        document.documentElement.classList.add('no-scroll');
        this.#setPageInert(true);
        this.overlay.close.focus();
        this.#updateOverlayScrollState();
    }

    closeOverlay() {
        if (!this.overlay || !this.isOverlayOpen()) return;

        const element = this.overlay.element;
        element.classList.replace('open', 'close');
        setTimeout(() => element.classList.remove('close', 'overflow-bottom'), 300);
        this.navButton.classList.remove('active');
        this.navButton.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('no-scroll');
        this.#setPageInert(false);

        if (element.contains(document.activeElement)) {
            this.navButton.focus();
        }
    }

    /**
     * Sperrt bei offenem Overlay den Rest der Seite für Tastatur und Screenreader (inert): alle Geschwister auf dem Weg
     * vom Overlay bis zum body, außer Skripten und der Cookiebar (eigener Fokusfang). Ohne die Sperre wandert der
     * Tab-Fokus aus dem Vollbild-Overlay in die unsichtbare Seite dahinter.
     *
     * @param {boolean} inert
     */
    #setPageInert(inert) {
        if (!this.overlay) return;

        if (!inert) {
            for (const element of this.#inertElements) {
                element.inert = false;
            }

            this.#inertElements = [];

            return;
        }

        for (let node = this.overlay.element; node.parentElement && node !== document.body; node = node.parentElement) {
            for (const sibling of node.parentElement.children) {
                if (
                    sibling === node ||
                    !(sibling instanceof HTMLElement) ||
                    sibling.inert ||
                    sibling.matches('script, .contao-cookiebar')
                ) {
                    continue;
                }

                sibling.inert = true;
                this.#inertElements.push(sibling);
            }
        }
    }

    /**
     * Setzt „overflow-bottom" am Overlay, solange im Scroll-Container noch Inhalt unterhalb des sichtbaren Bereichs liegt
     * (kleine Bildschirmhöhen). Das SCSS blendet damit einen Verlauf am unteren Rand ein — der Rahmen bleibt sichtbar,
     * der Inhalt läuft erkennbar weiter. Wird bei Öffnen, Scrollen und Fenstergröße aktualisiert.
     */
    #updateOverlayScrollState() {
        if (!this.overlay) return;

        const { element, scroller } = this.overlay;
        const hasMore = this.isOverlayOpen() && scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > 1;

        element.classList.toggle('overflow-bottom', hasMore);
    }

    /** Mobile Navigation bzw. Overlay umschalten — je nachdem, was konfiguriert ist. */
    toggleNavigation() {
        this.overlay ? this.toggleOverlay() : this.toggleMenu();
    }

    hasOpenLevels() {
        return this.submenus.some((submenu) => submenu.state.isOpen);
    }

    /**
     * @param {number} level
     */
    closeOpenLevels(level = 1) {
        for (const submenu of this.submenus) {
            if (submenu.state.isOpen && submenu.level >= level) {
                submenu.closeLevel();
            }
        }
    }

    /**
     * @param {string} selector
     *
     * @returns {HTMLElement}
     */
    #query(selector) {
        const element = document.querySelector(selector);

        if (!(element instanceof HTMLElement)) {
            throw new Error(`Could not find element with selector ${selector}`);
        }

        return element;
    }

    /**
     * @param {string} selector
     *
     * @returns {NodeListOf<HTMLAnchorElement>}
     */
    #queryAll(selector) {
        const element = document.querySelectorAll(selector);

        if (!(element instanceof NodeList)) {
            throw new Error(`Could not find element with selector ${selector}`);
        }

        /* @ts-ignore  */
        return element;
    }

    #getSubmenus() {
        for (const link of this.root.querySelectorAll('a.submenu, strong.submenu')) {
            this.submenus.push(new Submenu(link, this));
        }
    }

    #registerEvents() {
        if (this.overlay) {
            this.navButton.addEventListener('click', () => this.toggleOverlay());
            this.overlay.close.addEventListener('click', () => this.toggleOverlay());
            this.overlay.scroller.addEventListener('scroll', () => this.#updateOverlayScrollState(), { passive: true });
            window.addEventListener('resize', () => this.#updateOverlayScrollState());
        } else {
            this.navButton.addEventListener('click', () => this.toggleMenu());
        }

        if (Browser.isOnePage) {
            // Im Overlay-Modus schließen ALLE Sprunglinks des Overlays (Hauptnavigation und Aside, z. B. „Jetzt spenden"),
            // nicht nur die der Navigation — sonst bleibt das Overlay samt Scroll-Sperre (html.no-scroll) über dem Ziel stehen.
            const scope = this.overlay?.element ?? this.root;
            const links = scope.querySelectorAll('a:not(.submenu)[href*="#"]');

            for (const link of links) {
                link.addEventListener('click', () => this.#onLinkClick());
            }
        }

        window.addEventListener('click', (e) => this.#onClickOutside(e));
        window.addEventListener('resize', () => this.#onResize());
        document.addEventListener('keyup', (e) => this.#onEscape(e));

        this.skipNav.addEventListener('click', () => this.inMobileMode() && this.toggleNavigation());
    }

    /**
     * @param {KeyboardEvent} event
     */
    #onEscape(event) {
        if (event.key !== 'Escape') return;

        if (this.overlay) {
            this.closeOverlay();
        } else if (!this.inMobileMode()) {
            this.closeOpenLevels();
        }
    }

    #onLinkClick() {
        // In case of one-page navigation, close the menu/overlay when a link is clicked
        if (this.overlay) {
            this.closeOverlay();
        } else if (this.inMobileMode()) {
            this.toggleMenu();
        }
    }

    #onResize() {
        const currentMode = this.inMobileMode() ? 'mobile' : 'desktop';

        if (this.modeBeforeResize !== currentMode) {
            this.inMobileMode() ? this.openTrail() : this.#resetMobileMenu();

            this.modeBeforeResize = currentMode;
        }
    }

    /**
     * @param {MouseEvent} event
     */
    #onClickOutside(event) {
        if (this.inMobileMode()) {
            return;
        }

        if (
            event.target instanceof HTMLElement &&
            !this.root.contains(event.target) &&
            !this.navButton.contains(event.target)
        ) {
            this.closeOpenLevels();
        }
    }

    #resetMobileMenu() {
        this.closeOverlay(); // Fensterwechsel in den Desktop-Modus bei offenem Overlay
        this.#resetOpenLevels();
        this.root.style.display = '';
        this.navButton.classList.remove('active');
        this.header?.classList.remove('menu-open');
    }

    #resetOpenLevels() {
        for (const submenu of this.submenus) {
            if (submenu.state.isOpen) submenu.closeLevel(0);
        }
    }
}

class Submenu {
    /** The parent DropdownMenu instance. */
    parent;

    /** The link that opens the submenu (a) */
    link;

    /** The button that opens the submenu (button) */
    submenuBtn;

    /** The parent container of the link (li) */
    container;

    /** The submenu itself (ul) */
    menu;

    /** The duration of the slide animation */
    duration = 200;

    /** The level number of the submenu */
    level;

    state = {
        isOpen: false,
        isTrail: false,
        wasTouched: false,
        wasClicked: false,
    };

    /**
     * @param {Element} link
     * @param {DropdownMenu} parent
     */
    constructor(link, parent) {
        if (!(link instanceof HTMLAnchorElement || link.tagName === 'STRONG')) {
            throw new Error('Invalid link element for the submenu');
        }

        const container = link.parentElement;
        const menu = link.nextElementSibling;

        if (!(container instanceof HTMLLIElement) || !(menu instanceof HTMLUListElement)) {
            throw new Error('Invalid parent elements for the submenu');
        }

        this.parent = parent;
        this.link = link;
        this.container = container;
        this.menu = menu;

        const match = this.menu.className.match(/level_(\d+)/);
        this.level = match ? Number.parseInt(match[1]) : 0;

        this.submenuBtn = this.#addButton();

        if (this.link.classList.contains('trail')) {
            this.state.isTrail = true;
        }

        this.#registerEvents();
    }

    openLevel() {
        this.menu.classList.remove('show-left');

        if (!this.parent.inMobileMode() && this.level > 2) {
            const containerRect = this.container.getBoundingClientRect();
            const availableSpace = window.innerWidth - containerRect.right;

            if (availableSpace < 300) {
                this.menu.classList.add('show-left');
            }
        }

        slideDown(this.menu, this.duration);
        this.state.isOpen = true;
        this.submenuBtn.ariaExpanded = 'true';
        this.submenuBtn.ariaLabel = `Untermenü Level ${this.level} schließen`;
        this.link.classList.add('open');
        this.parent.header?.classList.add('level-open');
    }

    closeLevel(duration = this.duration) {
        slideUp(this.menu, duration, () => {
            this.link.classList.remove('open');
        });

        this.state.isOpen = false;
        this.submenuBtn.ariaExpanded = 'false';
        this.submenuBtn.ariaLabel = `Untermenü Level ${this.level} öffnen`;

        if (!this.parent.hasOpenLevels()) {
            this.parent.header?.classList.remove('level-open');
        }
    }

    toggleLevel() {
        this.state.isOpen ? this.closeLevel() : this.openLevel();
    }

    #addButton() {
        const div = document.createElement('div');
        div.classList.add('toggle-submenu-btn');

        const button = document.createElement('button');
        button.classList.add('invisible');
        button.type = 'button';
        button.ariaLabel = `Untermenü Level ${this.level} öffnen`;
        button.ariaHasPopup = 'true';
        button.ariaExpanded = 'false';

        div.appendChild(button);
        this.link.after(div);

        return button;
    }

    #registerEvents() {
        this.container.addEventListener('touchstart', () => this.#onTouch(), { passive: true});
        this.container.addEventListener('mouseenter', () => this.#onHover('enter'));
        this.container.addEventListener('mouseleave', () => this.#onHover('leave'));
        this.container.addEventListener('focusout', (e) => this.#onFocusOut(e));
        this.container.addEventListener('click', (e) => this.#onClick(e));
        this.submenuBtn.addEventListener('click', () => this.toggleLevel());
    }

    /**
     * Desktop - Touch
     * Close an open submenu when touched outside of it.
     *
     * @param {FocusEvent} event
     */
    #onFocusOut(event) {
        // Not in mobile mode
        if (this.parent.inMobileMode()) {
            return;
        }

        if (event.relatedTarget instanceof Node && this.state.isOpen && !this.container.contains(event.relatedTarget)) {
            this.closeLevel();
        }
    }

    /**
     * Mobile/Desktop - Touch
     * Check if a touch event was fired.
     */
    #onTouch() {
        this.state.wasTouched = true;
    }

    /**
     * Desktop - No Touch
     *
     * @param {'enter' | 'leave'} direction
     */
    #onHover(direction) {
        // Not in desktop mode
        // Not when there was a touch or click event before
        if (this.parent.inMobileMode() || this.state.wasTouched || this.state.wasClicked) {
            this.state.wasClicked = false;

            return;
        }

        direction === 'enter' ? this.openLevel() : this.closeLevel();
    }

    /**
     * Mobile/Desktop - Touch
     * Opens the submenu when touched.
     * We use the click event because it is more reliable across devices.
     *
     * @param {MouseEvent | PointerEvent} event
     */
    #onClick(event) {
        // Always if in mobile mode or on desktop if there was a touch event before
        if (!this.parent.inMobileMode() && !this.state.wasTouched) {
            return;
        }

        this.state.wasClicked = true;

        event.stopPropagation();

        if (event.target instanceof HTMLAnchorElement && !event.target.classList.contains('submenu')) {
            return;
        }

        event.preventDefault();

        if (this.state.isOpen) {
            this.closeLevel();
        } else {
            this.parent.closeOpenLevels(this.level);
            this.openLevel();
        }

        this.state.wasTouched = false;
    }
}
